<template>
  <div class="bg-primaryLight rounded-[12px] border border-grayLight p-6">
    <div class="flex items-center justify-between mb-4">
      <div>
        <p class="text-[14px] font-semibold text-bodyText">
          Productivity
        </p>
      </div>
      <div class="flex items-center gap-2 text-[11px] text-grayDark">
        <div class="flex gap-1">
          <div class="w-3 h-3 rounded-[2px] bg-[#EBEDF0]"></div>
          <div class="w-3 h-3 rounded-[2px] bg-[#C6CBD1]"></div>
          <div class="w-3 h-3 rounded-[2px] bg-[#667085]"></div>
          <div class="w-3 h-3 rounded-[2px] bg-[#475467]"></div>
          <div class="w-3 h-3 rounded-[2px] bg-[#222222]"></div>
        </div>
      </div>
    </div>

    <div class="mt-4">
      <div class="w-full">
        <!-- Month labels -->
        <div class="flex mb-2">
          <div class="w-8 flex-shrink-0"></div>
          <div class="flex-1 flex gap-[2px]">
            <div
              v-for="(week, wIdx) in heatmap"
              :key="wIdx"
              class="flex-1 overflow-visible"
            >
              <span
                v-if="monthLabels[wIdx]"
                class="text-[11px] text-grayDark whitespace-nowrap"
              >{{ monthLabels[wIdx] }}</span>
            </div>
          </div>
        </div>

        <div class="flex">
          <!-- Day labels -->
          <div class="w-8 flex-shrink-0 pr-2 text-[10px] text-grayDark flex flex-col justify-between">
            <div>Sun</div>
            <div></div>
            <div>Tue</div>
            <div></div>
            <div>Thu</div>
            <div></div>
            <div>Sat</div>
          </div>

          <!-- Heatmap grid -->
          <div class="flex-1 flex gap-[2px]">
            <div
              v-for="(week, wIdx) in heatmap"
              :key="wIdx"
              class="flex flex-col gap-[2px] flex-1"
            >
              <div
                v-for="(lvl, dIdx) in week"
                :key="dIdx"
                class="aspect-square w-full rounded-[2px] cursor-pointer hover:ring-2 hover:ring-primaryDark transition-all"
                :class="heatmapLevelClass(lvl)"
                :title="getHeatmapTooltip(wIdx, dIdx)"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";

export default {
  name: "ProductivityHeatmap",
  data() {
    return {
      productivity: {
        productivity_data: [],
        total_tasks_completed: 0,
        days_tracked: 0,
      },
      loadingProductivity: true,

      monthNames: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ],
    };
  },
  computed: {
    gridDates() {
      const today = new Date();
      const endDate = new Date(today);
      const dayOfWeek = endDate.getDay();
      endDate.setDate(endDate.getDate() + (6 - dayOfWeek));

      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - (52 * 7) + 1);

      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek !== 0) {
        startDate.setDate(startDate.getDate() - startDayOfWeek);
      }

      return { startDate, endDate };
    },

    countMap() {
      const map = {};
      this.productivity.productivity_data.forEach((item) => {
        map[item.date] = item.count || 0;
      });
      return map;
    },

    monthLabels() {
      const { startDate } = this.gridDates;
      const labels = {};
      let lastMonth = -1;

      for (let wIdx = 0; wIdx < this.heatmap.length; wIdx++) {
        const weekStart = new Date(startDate);
        weekStart.setDate(weekStart.getDate() + wIdx * 7);
        const month = weekStart.getMonth();

        if (month !== lastMonth) {
          labels[wIdx] = this.monthNames[month];
          lastMonth = month;
        }
      }

      return labels;
    },

    heatmap() {
      const { startDate, endDate } = this.gridDates;
      const weeks = [];
      let currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        const week = [];

        for (let day = 0; day < 7; day++) {
          const dateStr = currentDate.toISOString().split("T")[0];
          const count = this.countMap[dateStr] || 0;
          week.push(count);

          currentDate.setDate(currentDate.getDate() + 1);
        }

        weeks.push(week);

        if (weeks.length > 53) break;
      }

      return weeks;
    },

    totalTasksCompleted() {
      return this.productivity.total_tasks_completed || 0;
    },
  },
  methods: {
    countToLevel(count) {
      if (count <= 0) return 0;
      if (count <= 3) return 1;
      if (count <= 6) return 2;
      if (count <= 9) return 3;
      return 4;
    },

    heatmapLevelClass(count) {
      const level = this.countToLevel(count);
      switch (level) {
        case 0:
          return "bg-[#EBEDF0]";
        case 1:
          return "bg-[#C6CBD1]";
        case 2:
          return "bg-[#667085]";
        case 3:
          return "bg-[#475467]";
        case 4:
          return "bg-[#222222]";
        default:
          return "bg-[#EBEDF0]";
      }
    },

    getHeatmapTooltip(weekIndex, dayIndex) {
      const { startDate } = this.gridDates;

      const targetDate = new Date(startDate);
      targetDate.setDate(targetDate.getDate() + (weekIndex * 7) + dayIndex);

      const dateStr = targetDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      const count = this.heatmap[weekIndex]?.[dayIndex] || 0;
      const activityText =
        count === 0 ? "No tasks" : `${count} task${count !== 1 ? "s" : ""} completed`;

      return `${activityText} on ${dateStr}`;
    },

    async fetchProductivity() {
      try {
        this.loadingProductivity = true;
        const response = await apiClient.get(
          "/web/dashboard/manager/productivity",
          {
            params: {
              days: 365,
            },
          }
        );
        this.productivity = response.data || {
          productivity_data: [],
          total_tasks_completed: 0,
          days_tracked: 0,
        };
      } catch (error) {
        console.error("Error fetching productivity data:", error);
      } finally {
        this.loadingProductivity = false;
      }
    },
  },
  created() {
    this.fetchProductivity();
  },
};
</script>
